/**
 * AI theme generator dialog (#192).
 *
 * Opens from the ThemeTab editor's "Generate with AI" button. A brief
 * textarea + the shared `BudgetBanner` + a submit button. On success the
 * proposed `themeJson` is handed back to the parent via `onApply` so the
 * ThemeTab can populate its react-hook-form values — the dialog does NOT
 * save the theme itself. The operator still has to click "Save theme" in
 * the editor to PATCH the site.
 *
 * Error handling mirrors the AI Studio surface:
 *   - 429 `AI_RATE_LIMITED` / `AI_BUDGET_EXCEEDED` → error toast (the
 *     banner above the prompt also surfaces the budget state).
 *   - 422 `AI_INVALID_PLAN` → error toast with the planner's diagnostic
 *     message.
 *   - Other errors → generic error toast.
 *
 * MUI only — no Tailwind, CSS modules, or raw CSS per
 * `standards/react-patterns.md`.
 *
 * `data-testid` attributes for QA hooks:
 *   - `theme-tab-ai-dialog`
 *   - `theme-tab-ai-brief`
 *   - `theme-tab-ai-submit`
 *   - `theme-tab-ai-cancel`
 */

import { useState } from "react";
import type { ReactElement } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import type { ThemeJsonV1 } from "@ottopilot/shared";
import { themeJsonV1 } from "@ottopilot/shared";
import { useGenerateTheme } from "../../hooks/useAiStudio";
import { useToast } from "../../components/ToastProvider";
import { BudgetBanner } from "../ai/BudgetBanner";
import type { ApiClientError } from "../../lib/api";

/** Mirrors the server-side cap on `aiGenerateThemeSchema.prompt`. */
const PROMPT_MAX_LEN = 8_000;

export type ThemeGeneratorDialogProps = {
  open: boolean;
  onClose: () => void;
  /** The Site id whose theme is being generated. */
  siteId: string;
  /**
   * Called with the validated `ThemeJsonV1` proposal on success. The
   * parent ThemeTab uses this to seed its react-hook-form values — the
   * operator confirms by clicking "Save theme" in the editor.
   */
  onApply: (themeJson: ThemeJsonV1) => void;
};

export function ThemeGeneratorDialog({
  open,
  onClose,
  siteId,
  onApply,
}: ThemeGeneratorDialogProps): ReactElement {
  const toast = useToast();
  const [prompt, setPrompt] = useState("");
  const generate = useGenerateTheme(siteId);

  const trimmedLen = prompt.trim().length;
  const canSubmit = trimmedLen > 0 && trimmedLen <= PROMPT_MAX_LEN && !generate.isPending;

  const handleClose = () => {
    if (generate.isPending) return;
    setPrompt("");
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    generate.mutate(
      { prompt: prompt.trim() },
      {
        onSuccess: (data) => {
          // The API has already validated against `themeJsonV1`, but we
          // re-narrow on the client too so `onApply` receives the
          // strict-typed shape rather than the wire-level `unknown`.
          const parsed = themeJsonV1.safeParse(data.themeJson);
          if (!parsed.success) {
            toast.showError(
              "AI returned a theme that failed client-side validation. Please try again.",
            );
            return;
          }
          toast.showSuccess("Theme proposal applied. Review and click Save theme to persist.");
          onApply(parsed.data);
          setPrompt("");
          onClose();
        },
        onError: (err: ApiClientError) => {
          if (err.code === "AI_RATE_LIMITED") {
            toast.showError("Too many AI calls this minute. Please wait and try again.");
            return;
          }
          if (err.code === "AI_BUDGET_EXCEEDED") {
            toast.showError(
              "Monthly AI budget exceeded. Start an override window from the banner above.",
            );
            return;
          }
          if (err.code === "AI_INVALID_PLAN") {
            toast.showError(
              "AI returned a theme that failed validation. Try a more specific brief.",
            );
            return;
          }
          if (err.code === "SITE_NOT_FOUND") {
            toast.showError("Site not found. Refresh and try again.");
            return;
          }
          toast.showError(err.message || "Could not generate theme.");
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      data-testid="theme-tab-ai-dialog"
    >
      <DialogTitle>Generate theme with AI</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <BudgetBanner />
          <DialogContentText>
            Describe the brand and the AI will propose fonts, colours, and a corner radius. The
            proposal populates the editor; nothing is saved until you click <b>Save theme</b>.
          </DialogContentText>
          <Box>
            <TextField
              label="Brand brief"
              multiline
              minRows={5}
              maxRows={12}
              fullWidth
              autoFocus
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                "e.g. A wellness brand for a yoga studio — warm, calm, " +
                "natural palette with rounded corners. Friendly tone."
              }
              error={trimmedLen > PROMPT_MAX_LEN}
              helperText={
                trimmedLen > PROMPT_MAX_LEN
                  ? `Brief is ${String(trimmedLen)} characters — please trim to ${String(PROMPT_MAX_LEN)}.`
                  : `${String(trimmedLen)} / ${String(PROMPT_MAX_LEN)}`
              }
              inputProps={{ "data-testid": "theme-tab-ai-brief" }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={generate.isPending}
          data-testid="theme-tab-ai-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="theme-tab-ai-submit"
        >
          {generate.isPending ? "Generating…" : "Generate theme"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
